import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_LOCALES,
  ALL_PORTAL_PROJECT_SCOPES,
  PORTAL_PROJECT_SCOPE,
  type PortalAccountView,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { portalAccountsApi, projectsApi, type ClientContact } from '../../api/endpoints.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxGroupField,
  Dialog,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { MomentText } from '../reports/report-parts.js';

/**
 * One contact's client-portal ACCOUNT — Phase 11 (D146, contract P).
 *
 * The contact's portal switch records ELIGIBILITY; this panel manages the
 * separate account that eligibility allows. One account per contact, ever:
 * it is deactivated, never deleted, and deactivating one revokes its live
 * portal sessions without touching a single record.
 *
 * "Invitation requested" is exactly that: the identity provider was ASKED to
 * send a message. Nothing here claims it arrived (CLAUDE.md section 8).
 *
 * Shown only to holders of `portal_accounts.manage`; the server re-checks
 * every call regardless.
 */

/** Detail codes the portal-account routes answer with, named. */
const DETAIL_CODE_KEYS: Readonly<Record<string, string>> = {
  contact_not_portal_enabled: 'contactNotPortalEnabled',
  portal_account_exists: 'accountExists',
  projects_required: 'projectsRequired',
  projects_not_allowed_for_scope: 'projectsNotAllowedForScope',
  project_not_of_client: 'projectNotOfClient',
  already_deactivated: 'alreadyDeactivated',
  already_active: 'alreadyActive',
  account_deactivated: 'accountDeactivated',
};

export function PortalAccountError({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  if (error instanceof ApiError) {
    const named = error.details.find((detail) => detail.code && DETAIL_CODE_KEYS[detail.code]);
    if (named?.code) {
      return <Alert tone="warning" title={t(`portalAccounts.errors.${DETAIL_CODE_KEYS[named.code]}`)} />;
    }
    if (error.status === 409 && error.code === 'DUPLICATE_RECORD') {
      return <Alert tone="warning" title={t('portalAccounts.errors.emailInUse')} />;
    }
    if (error.status === 503) {
      return <Alert tone="warning" title={t('portalAccounts.errors.providerUnavailable')} />;
    }
  }
  return <ErrorNotice error={error} />;
}

type DialogKind = 'create' | 'edit' | 'deactivate' | 'reactivate' | null;

export function PortalAccountPanel({
  clientId,
  contact,
  account,
}: {
  readonly clientId: string;
  readonly contact: ClientContact;
  readonly account: PortalAccountView | undefined;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [scope, setScope] = useState<string | null>(null);
  const [projectIds, setProjectIds] = useState<readonly string[]>([]);
  const [preferredLocale, setPreferredLocale] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [showEvents, setShowEvents] = useState(false);

  const projects = useQuery({
    queryKey: ['projects', 'of-client', clientId],
    queryFn: () => projectsApi.list({ clientId, pageSize: 100 }),
    enabled: dialog === 'create' || dialog === 'edit',
  });

  const detail = useQuery({
    queryKey: ['portal-account', account?.id],
    queryFn: () => portalAccountsApi.get(account?.id ?? ''),
    enabled: showEvents && Boolean(account),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['portal-accounts', clientId] });
    await queryClient.invalidateQueries({ queryKey: ['portal-account', account?.id] });
  };

  const open = (kind: DialogKind) => {
    setScope(account?.projectScope ?? null);
    setProjectIds(account?.projects.map((project) => project.id) ?? []);
    setPreferredLocale(account?.preferredLocale ?? null);
    setEmail(account?.email ?? '');
    setReason('');
    act.reset();
    setDialog(kind);
  };

  const selected = scope === PORTAL_PROJECT_SCOPE.SELECTED_PROJECTS;

  const act = useMutation({
    mutationFn: async (): Promise<string> => {
      if (dialog === 'create') {
        await portalAccountsApi.create(clientId, contact.id, {
          ...(scope ? { projectScope: scope as PortalAccountView['projectScope'] } : {}),
          ...(selected ? { projectIds: [...projectIds] } : {}),
          ...(preferredLocale ? { preferredLocale: preferredLocale as PortalAccountView['preferredLocale'] } : {}),
        });
        return 'portalAccounts.created';
      }
      if (!account) return 'portalAccounts.saved';
      if (dialog === 'edit') {
        await portalAccountsApi.update(account.id, {
          ...(scope ? { projectScope: scope as PortalAccountView['projectScope'] } : {}),
          ...(selected ? { projectIds: [...projectIds] } : {}),
          ...(preferredLocale ? { preferredLocale: preferredLocale as PortalAccountView['preferredLocale'] } : {}),
          ...(email.trim() && email.trim() !== account.email ? { email: email.trim() } : {}),
        });
        return 'portalAccounts.saved';
      }
      if (dialog === 'deactivate') {
        await portalAccountsApi.deactivate(account.id, reason.trim());
        return 'portalAccounts.deactivated';
      }
      await portalAccountsApi.reactivate(account.id, reason.trim());
      return 'portalAccounts.reactivated';
    },
    onSuccess: async (messageKey) => {
      await refresh();
      show({ tone: 'success', message: t(messageKey) });
      setDialog(null);
    },
  });

  const invite = useMutation({
    mutationFn: () => portalAccountsApi.invite(account?.id ?? ''),
    onSuccess: async () => {
      await refresh();
      show({ tone: 'success', message: t('portalAccounts.invitationRequested') });
    },
  });

  const canSubmit =
    dialog === 'deactivate' || dialog === 'reactivate'
      ? reason.trim().length >= 3
      : !selected || projectIds.length > 0;

  return (
    <div className="flex flex-col gap-2 border-t border-default-200 pt-3">
      <p className="text-caption font-medium text-default-700">{t('portalAccounts.title')}</p>

      {account ? (
        <>
          <div className="flex flex-wrap items-center gap-inline">
            <StatusBadge
              size="sm"
              tone={account.isActive ? 'info' : 'neutral'}
              label={account.isActive ? t('portalAccounts.active') : t('portalAccounts.deactivatedBadge')}
            />
            <span className="text-caption text-default-600">
              {t(`portalAccounts.projectScope.${account.projectScope}`)}
            </span>
          </div>
          {account.projects.length > 0 ? (
            <p className="text-caption text-default-600">
              {new Intl.ListFormat(locale).format(
                account.projects.map(
                  (project) => project.name ?? t('common.notRecorded'),
                ),
              )}
            </p>
          ) : null}
          <dl className="grid grid-cols-1 gap-1 text-caption text-default-600">
            <div>
              <dt className="inline">{t('portalAccounts.invitationRequestedAt')}: </dt>
              <dd className="inline">
                {account.invitationRequestedAt ? (
                  <MomentText at={account.invitationRequestedAt} />
                ) : (
                  t('portalAccounts.noInvitation')
                )}
              </dd>
            </div>
            <div>
              <dt className="inline">{t('portalAccounts.lastLoginAt')}: </dt>
              <dd className="inline">
                {account.lastLoginAt ? <MomentText at={account.lastLoginAt} /> : t('portalAccounts.neverSignedIn')}
              </dd>
            </div>
            {account.deactivation ? (
              <div>
                <dt className="inline">{t('portalAccounts.deactivationReason')}: </dt>
                <dd className="inline">{account.deactivation.reason}</dd>
              </div>
            ) : null}
          </dl>

          <ActionBar>
            {account.isActive ? (
              <>
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  isLoading={invite.isPending}
                  onPress={() => invite.mutate()}
                >
                  {account.invitationRequestedAt
                    ? t('portalAccounts.resendInvitation')
                    : t('portalAccounts.sendInvitation')}
                </ActionButton>
                <ActionButton priority="tertiary" size="sm" onPress={() => open('deactivate')}>
                  {t('portalAccounts.deactivate')}
                </ActionButton>
              </>
            ) : (
              <ActionButton priority="tertiary" size="sm" onPress={() => open('reactivate')}>
                {t('portalAccounts.reactivate')}
              </ActionButton>
            )}
            <ActionButton priority="secondary" size="sm" onPress={() => open('edit')}>
              {t('portalAccounts.editAccess')}
            </ActionButton>
            <ActionButton priority="tertiary" size="sm" onPress={() => setShowEvents((value) => !value)}>
              {showEvents ? t('portalAccounts.hideEvents') : t('portalAccounts.showEvents')}
            </ActionButton>
          </ActionBar>
          <PortalAccountError error={invite.error} />

          {showEvents ? (
            detail.error ? (
              <ErrorNotice error={detail.error} />
            ) : detail.data && detail.data.accessEvents.length === 0 ? (
              <p className="text-caption text-default-600">{t('portalAccounts.noEvents')}</p>
            ) : (
              <ul className="flex flex-col gap-1 text-caption text-default-700">
                {(detail.data?.accessEvents ?? []).map((event) => (
                  <li key={event.id}>
                    {t(`portalAccounts.events.${event.kind}`)} · <MomentText at={event.occurredAt} />
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </>
      ) : contact.portalEnabled && contact.email && contact.isActive ? (
        <ActionBar>
          <ActionButton priority="secondary" size="sm" onPress={() => open('create')}>
            {t('portalAccounts.create')}
          </ActionButton>
        </ActionBar>
      ) : (
        <p className="text-caption text-default-600">{t('portalAccounts.notEligible')}</p>
      )}

      <Dialog
        isOpen={dialog !== null}
        onClose={() => setDialog(null)}
        title={
          dialog === 'create'
            ? t('portalAccounts.create')
            : dialog === 'deactivate'
              ? t('portalAccounts.deactivate')
              : dialog === 'reactivate'
                ? t('portalAccounts.reactivate')
                : t('portalAccounts.editAccess')
        }
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setDialog(null)} isDisabled={act.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!canSubmit}
              isLoading={act.isPending}
              onPress={() => act.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {dialog === 'create' || dialog === 'edit' ? (
            <>
              {dialog === 'create' ? (
                <Alert
                  tone="info"
                  title={t('portalAccounts.createNoticeTitle')}
                  description={t('portalAccounts.createNoticeBody')}
                />
              ) : (
                <TextField
                  label={t('portalAccounts.email')}
                  description={t('portalAccounts.emailHint')}
                  value={email}
                  onChange={setEmail}
                />
              )}
              <SelectField
                label={t('portalAccounts.projectScopeLabel')}
                description={dialog === 'create' ? t('portalAccounts.defaultApplies') : undefined}
                value={scope}
                onChange={setScope}
                placeholder={t('portalAccounts.laboratoryDefault')}
                options={ALL_PORTAL_PROJECT_SCOPES.map((value) => ({
                  value,
                  label: t(`portalAccounts.projectScope.${value}`),
                }))}
              />
              {selected ? (
                <CheckboxGroupField
                  label={t('portalAccounts.projectsLabel')}
                  description={
                    (projects.data?.data.length ?? 0) === 0 ? t('portalAccounts.noProjects') : undefined
                  }
                  value={projectIds}
                  onChange={setProjectIds}
                  options={(projects.data?.data ?? []).map((project) => ({
                    value: project.id,
                    label: project.name ?? project.reference ?? project.id,
                  }))}
                />
              ) : null}
              <SelectField
                label={t('portalAccounts.preferredLocale')}
                value={preferredLocale}
                onChange={setPreferredLocale}
                placeholder={t('portalAccounts.laboratoryDefault')}
                options={ALL_LOCALES.map((value) => ({
                  value,
                  label: t(`portalAccounts.locale.${value}`),
                }))}
              />
            </>
          ) : null}

          {dialog === 'deactivate' ? (
            <Alert
              tone="warning"
              title={t('portalAccounts.deactivateNoticeTitle')}
              description={t('portalAccounts.deactivateNoticeBody')}
            />
          ) : null}
          {dialog === 'deactivate' || dialog === 'reactivate' ? (
            <TextAreaField
              label={t('common.reason')}
              value={reason}
              onChange={setReason}
              isRequired
              rows={2}
            />
          ) : null}

          <PortalAccountError error={act.error} />
        </div>
      </Dialog>
    </div>
  );
}
